import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MindMapContext } from "../../../Context/contex";
import { emitLiveComments, listenerLiveComments, listenerLiveCommentsOff } from "../../../Socket/socket";
import { ChevronGlyph } from "../chatIcons";
import ChatAvatar from "./ChatAvatar";
import ChatComposer from "./ChatComposer";
import ChatEmptyState from "./ChatEmptyState";
import { buildRows, formatTime } from "./chatRows";
import { useChatScroll } from "./useChatScroll";
import classes from "./Chats.module.css";

const JUMP_ICON_SIZE = 14;
const JUMP_ICON_STROKE = 2.2;

const MessageRow = ({ message, isSelf, startsGroup, timestamp }) => (
  <div className={`${classes.row} ${isSelf ? classes.rowSelf : classes.rowOther} ${startsGroup ? classes.rowGroupStart : ""}`}>
    {!isSelf && <div className={classes.avatarSlot}>{startsGroup && <ChatAvatar user={message.userInfo} />}</div>}
    <div className={classes.bubbleColumn}>
      {!isSelf && startsGroup && <span className={classes.senderName}>{message.userInfo?.name || "Guest"}</span>}
      <div className={`${classes.bubble} ${isSelf ? classes.bubbleSelf : classes.bubbleOther}`}>
        <span className={classes.bubbleText}>{message.msg}</span>
        {timestamp && (
          <time className={classes.timestamp} dateTime={new Date(timestamp).toISOString()}>{formatTime(timestamp)}</time>
        )}
      </div>
    </div>
  </div>
);

/**
 * The live comments: messages grouped by sender burst, and the composer.
 * Comments are not stored; the list holds what arrived during this session.
 */
export const Chats = ({ id, isOpen, onIncomingMessage }) => {
  const { userInfo } = MindMapContext();
  const [messages, setMessages] = useState([]);
  const inputRef = useRef(null);
  const { listRef, hasNewBelow, scrollToBottom, handleScroll, pinToBottom } = useChatScroll(messages);
  // The socket listener is registered once, so it reaches the current callback through a ref.
  const onIncomingMessageRef = useRef(onIncomingMessage);
  onIncomingMessageRef.current = onIncomingMessage;

  const appendMessage = useCallback((message) => setMessages((previous) => [...previous, message]), []);

  useEffect(() => {
    listenerLiveComments((data) => {
      appendMessage({ ...data, self: false, receivedAt: Date.now() });
      onIncomingMessageRef.current?.();
    });
    return listenerLiveCommentsOff;
  }, [appendMessage]);

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom("auto");
    inputRef.current?.focus();
  }, [isOpen, scrollToBottom]);

  const sendMessage = (text) => {
    // color/margin stay on the payload for older clients that style from them;
    // this client lays messages out from `self`.
    const chatData = { id, msg: text, color: "#e9e9e9", margin: "5px 0px 5px auto", userInfo, time: Date.now() };
    pinToBottom();
    appendMessage({ ...chatData, self: true });
    emitLiveComments(chatData);
  };

  const rows = useMemo(() => buildRows(messages, userInfo?.name), [messages, userInfo]);

  return (
    <>
      <div className={classes.list} ref={listRef} onScroll={handleScroll} role="log" aria-live="polite">
        {rows.length === 0 ? <ChatEmptyState /> : rows.map(({ key, ...row }) => <MessageRow key={key} {...row} />)}
      </div>

      {hasNewBelow && (
        <button type="button" className={classes.jumpButton} onClick={() => scrollToBottom()}>
          New messages
          <ChevronGlyph size={JUMP_ICON_SIZE} strokeWidth={JUMP_ICON_STROKE} />
        </button>
      )}

      <ChatComposer ref={inputRef} onSend={sendMessage} />
    </>
  );
};
